import type { UserNotification } from '~/hooks/queries/use-notifications';

/**
 * FRONT-2363: the bell items for background work that was skipped because a
 * login died — EDDY's own (`reauth_required`, dedupe `cron_auth`) or a
 * connector's (`connector_reauth`). The backend keeps the skipped jobs in the
 * item's `action.payload`; this module reads that untrusted JSON and decides
 * what each row offers. Pure, so the static-markup suite can pin it.
 *
 * Job ids in the payload are display hints: Run now goes through the job's
 * own owner-checked run endpoint, which is the real authority.
 */

export type SkippedRunType = 'cron' | 'workflow' | 'app';

export interface SkippedRunEntry {
  type: SkippedRunType;
  id: string;
  name: string;
  at: string;
  outcome?: 'started' | 'gone';
}

export type BackgroundAuthVariant =
  | 'connector-broken'
  | 'connector-recovered'
  | 'login-broken'
  | 'login-recovered';

export interface BackgroundAuthItem {
  variant: BackgroundAuthVariant;
  providerKey?: string;
  providerLabel?: string;
  /** FRONT-846: the provider is not connected at all, so the action is Connect, not Reconnect. */
  notConnected?: boolean;
  skipped: SkippedRunEntry[];
}

/** A dead credential the backend couldn't name a provider for. */
export const UNKNOWN_PROVIDER_KEY = 'unknown';

/** The bell shows this many jobs before "and N more". */
export const SKIPPED_PREVIEW_LIMIT = 5;

const TYPES = new Set(['cron', 'workflow', 'app']);

function readEntry(value: unknown): SkippedRunEntry | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.type !== 'string' || !TYPES.has(v.type)) return null;
  if (typeof v.id !== 'string' || !v.id) return null;
  if (typeof v.at !== 'string') return null;
  const entry: SkippedRunEntry = {
    type: v.type as SkippedRunType,
    id: v.id,
    name: typeof v.name === 'string' && v.name ? v.name : v.id,
    at: v.at,
  };
  if (v.outcome === 'started' || v.outcome === 'gone') entry.outcome = v.outcome;
  return entry;
}

/**
 * The background-auth reading of a notification, or null for every other
 * kind. A `reauth_required` row written before FRONT-2363 has no payload and
 * stays a plain item with its generic Re-auth all action, exactly as before.
 */
export function readBackgroundAuthItem(n: Pick<UserNotification, 'kind' | 'action'>): BackgroundAuthItem | null {
  const payload = n.action?.payload;
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const recovered = p.state === 'recovered';
  const skipped = Array.isArray(p.skipped)
    ? p.skipped.map(readEntry).filter((e): e is SkippedRunEntry => e !== null)
    : [];
  if (n.kind === 'connector_reauth') {
    const providerKey = typeof p.provider === 'string' ? p.provider : undefined;
    if (!providerKey) return null;
    return {
      variant: recovered ? 'connector-recovered' : 'connector-broken',
      providerKey,
      providerLabel: typeof p.providerLabel === 'string' && p.providerLabel ? p.providerLabel : providerKey,
      ...(p.flavour === 'connect' ? { notConnected: true } : {}),
      skipped,
    };
  }
  if (n.kind === 'reauth_required') {
    return { variant: recovered ? 'login-recovered' : 'login-broken', skipped };
  }
  return null;
}

export function isRecovered(item: BackgroundAuthItem): boolean {
  return item.variant === 'connector-recovered' || item.variant === 'login-recovered';
}

/** What a skipped job's row offers once the login is back. */
export type SkippedRunAction =
  | { kind: 'run'; url: string }
  /** App work is driven by whoever uses the app; nothing to re-run. */
  | { kind: 'none' };

export function skippedRunAction(entry: Pick<SkippedRunEntry, 'type' | 'id'>): SkippedRunAction {
  const id = encodeURIComponent(entry.id);
  if (entry.type === 'cron') return { kind: 'run', url: `/api/crons/${id}/run` };
  if (entry.type === 'workflow') {
    // Through EDDY's platform proxy, which runs it as the signed-in user; the
    // workflow service refuses anyone without access.
    return { kind: 'run', url: `/api/platform/workflow/api/v1/workflows/${id}/runs` };
  }
  return { kind: 'none' };
}

/** Where a job lives, for "Open" after a Run now that could not start. */
export function skippedRunHref(entry: Pick<SkippedRunEntry, 'type' | 'id'>): string | null {
  const id = encodeURIComponent(entry.id);
  if (entry.type === 'cron') return `/crons/${id}`;
  if (entry.type === 'workflow') return `/workflows/${id}`;
  return null;
}

/**
 * A failed Run now that means the job is not there for this user any more:
 * deleted (404), no longer shared with them (403), or archived (409). The row
 * then reads "No longer exists" and stops counting. Anything else is a real
 * failure the user can retry or open.
 */
export function isGoneStatus(status: number | undefined): boolean {
  return status === 404 || status === 403 || status === 409;
}

/** Jobs a recovered item can still Run now for. */
export function runnableEntries(skipped: SkippedRunEntry[]): SkippedRunEntry[] {
  return skipped.filter((e) => !e.outcome && skippedRunAction(e).kind === 'run');
}

export function skippedKindLabel(type: SkippedRunType): string {
  if (type === 'workflow') return 'Workflow';
  if (type === 'app') return 'App';
  return 'Job';
}

/** "07:00" today, "Sep 27" otherwise — the skip time as a person reads it. */
export function formatSkippedAt(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
